<template>
    <div>
        <div v-if="isPending" class="flex flex-col items-center justify-center gap-3 rounded-lg border border-gray-200 bg-white p-16">
            <Spinner class="h-10 w-10" />
            <span class="text-sm text-gray-500">{{ $t('views.farmToFarm.computing') }}</span>
        </div>

        <div v-else-if="isError" class="flex flex-col items-center justify-center gap-4 rounded-lg border border-gray-200 bg-white p-16">
            <p class="text-sm text-red-600">{{ $t('views.farmToFarm.reportError') }}</p>
            <Button variant="outline" size="sm" @click="$emit('reset')">
                {{ $t('views.farmToFarm.startOver') }}
            </Button>
        </div>

        <MainViewCard v-else-if="report" class="overflow-hidden p-4">
            <div class="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div class="flex flex-wrap items-center gap-1">
                    <button
                        v-for="period in report.periods"
                        :key="period"
                        type="button"
                        class="border px-3 py-1.5 text-sm"
                        :class="period === selectedPeriod ? 'border-primary-500 bg-dark-gray-200 font-semibold text-primary-700' : 'border-dark-gray-200 bg-white text-dark-gray-600'"
                        @click="selectedPeriod = period"
                    >
                        {{ periodLabel(period) }}
                    </button>
                </div>

                <div class="flex items-center gap-2">
                    <Button variant="outline" size="sm" class="whitespace-nowrap" @click="$emit('reset')">
                        {{ $t('views.farmToFarm.startOver') }}
                    </Button>
                    <Button size="sm" class="whitespace-nowrap" @click="isExportOpen = true">
                        {{ $t('views.farmToFarm.export.button') }}
                    </Button>
                </div>
            </div>

            <FarmToFarmTable :report="report" :period="selectedPeriod" :aggregate-label="aggregateLabel" />

            <ExportModal v-model="isExportOpen" :periods="report.periods" :current-period-label="periodLabel(selectedPeriod)" :is-working="isExporting" @download="onDownload" />
        </MainViewCard>
    </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { useToast } from 'vue-toastification';
import MainViewCard from '@/components/common/MainViewCard.vue';
import { Button } from '@/components/ui/button';
import Spinner from '@/components/ui/Spinner.vue';
import { store } from '@/plugins/store';
import { useFarmToFarmLocations, useFarmToFarmReport } from '@/plugins/store/actions/queries/farm-to-farm';
import { buildFarmToFarmWorkbook } from '@/util/farmToFarmWorkbook';
import { downloadBlob } from '@/util/downloadFile';
import { FarmToFarmReport, FarmToFarmSelection } from '@/types';
import FarmToFarmTable from './FarmToFarmTable.vue';
import ExportModal from './ExportModal.vue';

/**
 * Step three: the benchmark table, one period at a time.
 *
 * Every selected period arrives in a single response, so switching periods is instant and the
 * export needs no further request. The request itself is fired once on mount rather than as a
 * reactive query - it can run for minutes, and refetching it behind the user's back would be
 * expensive and surprising.
 */
export default defineComponent({
    components: { Button, ExportModal, FarmToFarmTable, MainViewCard, Spinner },
    props: {
        /** Complete by construction - FarmToFarmSteps narrows the wizard draft before this step. */
        selection: {
            type: Object as PropType<FarmToFarmSelection>,
            required: true,
        },
    },
    emits: ['reset'],
    setup() {
        const { mutateAsync, isPending, isError } = useFarmToFarmReport();
        // Cached by the farm picker moments ago, and the only thing still wanted from the tree is
        // the group's name for the export filename - derived rather than carried through the
        // wizard, so it cannot go stale against a tree refetched in another language.
        const { data: locations } = useFarmToFarmLocations();
        return { mutateAsync, isPending, isError, locations };
    },
    data() {
        return {
            report: null as FarmToFarmReport | null,
            selectedPeriod: '',
            isExportOpen: false,
            isExporting: false,
            toast: useToast(),
        };
    },
    computed: {
        aggregateLabel(): string {
            // The rollup column is labelled with the customer's own name, matching how the
            // competitor's report presents it.
            return store.getters.activeDirectoryName || this.$t('views.farmToFarm.table.allFarms');
        },
        /** Only used to name the exported workbook. */
        animalGroupName(): string {
            return (this.locations ?? []).find((group) => group.animal_group_id === this.selection.animalGroupId)?.name ?? '';
        },
    },
    async created() {
        let report: FarmToFarmReport;
        try {
            report = await this.mutateAsync(this.selection);
        } catch {
            return;
        }

        this.report = report;
        // Open on the most recent period: it is the one a manager looks at first.
        this.selectedPeriod = report.periods[report.periods.length - 1] ?? '';
    },
    methods: {
        /**
         * The period as a reader sees it.
         *
         * `report.periods` carries wire keys only, so the labels come from the picker by way of the
         * wizard selection. They are not rebuilt here: Intl has no quarter or half-year format, and
         * duplicating the wording would put "1. kvartal" in twelve locale files as well as the API.
         *
         * The key is the fallback, so an unexpected period still renders as something rather than
         * as an empty tab.
         */
        periodLabel(period: string): string {
            return this.selection.periodLabels[period] ?? period;
        },
        async onDownload(scope: 'all' | 'current') {
            if (!this.report) return;

            this.isExporting = true;
            try {
                const periods = scope === 'all' ? this.report.periods : [this.selectedPeriod];
                const suffix = scope === 'all' ? `${periods[0]}_${periods[periods.length - 1]}` : this.selectedPeriod;

                const blob = await buildFarmToFarmWorkbook({
                    report: this.report,
                    periods,
                    aggregateLabel: this.aggregateLabel,
                    labelHeader: this.$t('views.farmToFarm.table.labelHeader'),
                    unitHeader: this.$t('views.farmToFarm.table.unitHeader'),
                    periodLabel: (period) => this.periodLabel(period),
                });

                // The filename keeps the wire keys, so downloads sort chronologically in a folder
                // listing and do not change shape with the exporter's language.
                downloadBlob(blob, `farm-to-farm_${this.animalGroupName || 'report'}_${suffix}.xlsx`);

                this.isExportOpen = false;
            } catch (error) {
                // Without this the workbook silently fails to appear and the click looks dead,
                // which is exactly how the earlier miswired dialog presented itself.
                this.toast.error(this.$t('views.farmToFarm.export.error'));
            } finally {
                this.isExporting = false;
            }
        },
    },
});
</script>
